<template>
  <v-dialog persistent v-model="dialog" max-width="700">
    <v-form ref="form" v-model="valid" :lazy-validation="lazy">
      <v-card class="mx-auto" :loading="loading">
        <v-card-title>Generazione report</v-card-title>
        <v-card-subtitle>Genera un nuovo report delle attività</v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col xl="2" lg="2" md="2" sm="2" class="hidden-xs-only">
              <v-subheader>Data</v-subheader>
            </v-col>
            <v-col xl="5" lg="5" md="5" sm="5" xs="6">
              <v-menu ref="refFromPickerMenu" v-model="fromPicker" :close-on-content-click="false" transition="scale-transition" min-width="290px" offset-y>
                <template v-slot:activator="{ on }">
                  <v-text-field :value="formattedFrom" v-on="on" placeholder="Inizio" :rules="requiredRules" single-line filled dense required clearable></v-text-field>
                </template>
                <v-date-picker v-model="report.from" locale="it-it" :max="report.to" no-title scrollable @change="fromPicker = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col xl="5" lg="5" md="5" sm="5" xs="6">
              <v-menu ref="refToPickerMenu" v-model="toPicker" :close-on-content-click="false" transition="scale-transition" min-width="290px" offset-y>
                <template v-slot:activator="{ on }">
                  <v-text-field :value="formattedTo" v-on="on" placeholder="Fine" :rules="requiredRules" single-line filled dense required clearable></v-text-field>
                </template>
                <v-date-picker v-model="report.to" locale="it-it" :min="report.from" no-title scrollable @change="toPicker = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-row style="margin-top: -25px;">
            <v-col xl="2" lg="2" md="2" sm="2" class="hidden-xs-only">
              <v-subheader>Area</v-subheader>
            </v-col>
            <v-col xl="10" lg="10" md="10" sm="10" xs="12">
              <v-select v-model="report.areaId" placeholder="Tutte le aree di pertinenza" :items="areas" item-value="id" item-text="description" 
                no-data-text="Nessuna area presente" @change="selectActivities()" single-line filled dense required clearable>
              </v-select>
            </v-col>
          </v-row>

          <v-row style="margin-top: -25px;">
            <v-col xl="2" lg="2" md="2" sm="2" class="hidden-xs-only">
              <v-subheader>Attività</v-subheader>
            </v-col>
            <v-col xl="10" lg="10" md="10" sm="10" xs="12">
              <v-select v-model="report.activityId" placeholder="Tutte le attività" :items="activities" item-value="id" item-text="description" 
                no-data-text="Nessuna attività presente" single-line filled dense required clearable>
              </v-select>
            </v-col>
          </v-row>

          <v-row style="margin-top: -25px;" v-if="isAdmin">
            <v-col xl="2" lg="2" md="2" sm="2" class="hidden-xs-only">
              <v-subheader>Utente</v-subheader>
            </v-col>
            <v-col xl="10" lg="10" md="10" sm="10" xs="12">
              <v-select v-model="report.userId" placeholder="Tutte gli utenti" :items="users" item-value="id" item-text="fullName" 
                no-data-text="Nessuna utente configurato" single-line filled dense required clearable>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small dark color="red" @click="clear()">Annulla</v-btn>
          <v-btn small dark color="green" :disabled="!(valid)" :loading="loading" @click="generate()">Genera</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import apiRepository from '../../api/api-repository';

moment.locale('it');

export default {
  name: 'ReportForm',
  props: [ 'report' ],

  data: ()=> ({
    // flag di gestione del componente.
    lazy: true, // tipo di validazione del form.
    valid: true, // stato di validazione del form.
    dialog: false, // apre/chiude la dialog contenente il form.
    loading: false, // abilita/disabilita il caricamento durante l'operazione.
    fromPicker: false, // apre/chiude il calendario della data di inizio.
    toPicker: false,  // apre/chiude il calendario della data di fine.
    // liste di selezione.
    areas: new Array(), // lista delle aree di pertinenza.
    activities: new Array(), // lista delle attività degli utenti.
    users: new Array(), // lista degli utenti attivi della scuola.
    // regole per i campi obbligatori.
    requiredRules: [ (v)=> !!v || 'Campo obbligatorio' ], // campo obbligatorio.
  }),

  computed: {
    ...mapGetters('oidcStore', [ 'oidcUser' ]),
      
    formattedFrom(){
        return (this.report.from) ? moment(this.report.from).format('DD/MM/YYYY') : '';
    },

    formattedTo(){
        return (this.report.to) ? moment(this.report.to).format('DD/MM/YYYY') : '';
    },

    isAdmin(){
        return (this.oidcUser.licenses.includes('WTR') && this.oidcUser.roles.includes('wtr.admin'));
    }
  },

  methods: {
    open(){
      this.init();
      if(this.$refs.form) this.$refs.form.resetValidation(); // resetto la validazione del form.
      this.valid = true; // setto il flag di validità del form.
      this.dialog = true; // apro la dialog contenente il form.
    },

    clear(){
      this.dialog = false; // chiudo la dialog contenente il form.
      this.$refs.form.resetValidation(); // resetto la validazione del form.
    },

    async generate(){
      // se il form non è valido esco dal salvataggio.
      if(!this.$refs.form.validate()) return;
      // form valido, proseguo con il salvataggio.
      this.loading = true; // setto il form il caricamento.
      // preparo la richiesta per la generazione del report.
      const req = { 
        type: 'CUSTOM',
        from: this.report.from, to: this.report.to, 
        areaId: (this.report.areaId && this.report.areaId.length > 0) ? this.report.areaId : null,
        activityId: (this.report.activityId && this.report.activityId.length > 0) ? this.report.activityId : null 
      };

      if(this.isAdmin) req.userId = (this.report.userId && this.report.userId.length > 0) ? this.report.userId : null;

      try{
        // genero il report con le caratteristiche richieste.
        const res = await apiRepository.report().generate(req); // attendo la risposta dalla api.
        this.loading = false; // sblocco la dialog dallo stato di caricamento.
        apiRepository.report().download(res); // scarico l'allegato dalla response.
        this.clear(); // pulisco la dialog e chiudo.
      }
      catch(e){
        // la generazione del report è andata in errore.
        console.lerror('exception:', e); // loggo l'errore riscontrato.
      }
    },

    async selectActivities(){
      this.report.activityId = null; // resetto il valore dell'attività precedentemente selezionata.
      const res = await apiRepository.activities().findByAreaId(this.report.areaId); // attendo la risposta dalla api.
      this.activities = res.data; // assegno alla lista del componente le attività trovate.
    },

    async init(){
      // recupero le aree di pertinenza dalle api.
      const areaRes = await apiRepository.area().findAll(); // attendo la risposta dalla api.
      this.areas = areaRes.data; // assegno alla lista del componente le aree trovate.
      
      if(this.report.areaId){
        // recupero le attività dell'area selezionata.
        const activityRes = await apiRepository.activities().findByAreaId(this.report.areaId);
        this.activities = activityRes.data;
      }

      if(this.isAdmin){
        // recupero gli utenti associati alla scuola dalle api.
        const userRes = await apiRepository.users().findAll(); // attendo la risposta dalla api.
        this.users = userRes.data; // assegno alla lista del componente gli utenti trovati.
      }
    }
  }
}
</script>